import { Autocomplete, MenuItem, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { readProduct, searchProducts } from '../../api/business/endpoints';
import type { IntegrationMapping, MappingDecision, ProductSummary } from '../../api/business/types';
import { formatDate } from '../../components/format';
import { FormDialog } from '../../components/FormDialog';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { batchLabel } from '../../components/batchLabel';
import { isVariable, variantLabel } from '../../components/variants';
import { useLanguage } from '../../i18n/LanguageContext';

/** `''` the whole product (orders issued FEFO), `new` a batch of the shop item's expiry, else a batch id. */
type BatchChoice = '' | 'new' | string;

type MappingDialogProps = {
  companyId: string;
  mapping: IntegrationMapping;
  onClose: () => void;
  onSubmit: (decision: MappingDecision) => void;
  isBusy: boolean;
  error: unknown;
};

/** Which app product — and batch — a shop item is, decided by the owner (ADR-0007 §3). */
export const MappingDialog = ({
  companyId,
  mapping,
  onClose,
  onSubmit,
  isBusy,
  error,
}: MappingDialogProps) => {
  const { language, t } = useLanguage();
  const [term, setTerm] = useState(mapping.shopReference ?? mapping.shopEan ?? '');
  const [product, setProduct] = useState<ProductSummary | null>(null);
  const [batch, setBatch] = useState<BatchChoice | null>(null);
  /** `''` the whole product, else a variant id — asked only for a variable product. */
  const [variant, setVariant] = useState<string | null>(null);
  const debounced = useDebouncedValue(term);

  const products = useQuery({
    queryKey: ['mapping-products', companyId, debounced],
    queryFn: () =>
      searchProducts(companyId, {
        term: debounced,
        status: 'ACTIVE',
        page: 1,
        sort: 'name',
        direction: 'asc',
      }),
  });
  const detail = useQuery({
    queryKey: ['product', companyId, product?.id],
    enabled: product !== null,
    queryFn: () => readProduct(companyId, product?.id ?? ''),
  });

  const variable = detail.data !== undefined && isVariable(detail.data);
  const variantChoice: string =
    variant ??
    (detail.data?.variants.some((one) => one.id === mapping.variantId) && mapping.productId === product?.id
      ? (mapping.variantId ?? '')
      : '');
  const batches = (detail.data?.batches ?? []).filter(
    (one) => !variable || variantChoice === '' || one.variantId === variantChoice,
  );
  const sameExpiry = batches.find(
    (one) => one.expiryDate !== null && one.expiryDate === mapping.shopExpiryDate,
  );
  // An expiry combination is a batch; the default is the batch of its date, or a new one of it.
  const defaultChoice: BatchChoice = mapping.shopExpiryDate === null ? '' : (sameExpiry?.id ?? 'new');
  const choice: BatchChoice = batch ?? defaultChoice;

  const submit = (): void => {
    if (product !== null) {
      const variantId: string | null = variable && variantChoice !== '' ? variantChoice : null;

      onSubmit(
        choice === 'new'
          ? { action: 'MAP', productId: product.id, variantId, createBatch: true }
          : { action: 'MAP', productId: product.id, variantId, batchId: choice === '' ? null : choice },
      );
    }
  };

  return (
    <FormDialog
      title={t('mapping.title')}
      submitLabel={t('mapping.map')}
      onClose={onClose}
      onSubmit={submit}
      isBusy={isBusy || product === null}
      error={error}
    >
      <Typography variant="body2">
        {t('mapping.shopItem', {
          name: mapping.shopName,
          id:
            mapping.shopVariantId === null
              ? mapping.shopProductId
              : `${mapping.shopProductId}/${mapping.shopVariantId}`,
        })}
        {mapping.shopExpiryDate
          ? ` · ${t('batch.expires', { date: formatDate(mapping.shopExpiryDate, language) })}`
          : ''}
      </Typography>
      <Autocomplete
        options={products.data?.items ?? []}
        value={product}
        loading={products.isFetching}
        filterOptions={(options) => options}
        getOptionLabel={(option) => [option.name, option.sku, option.ean].filter(Boolean).join(' · ')}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_event, value) => {
          setProduct(value);
          setBatch(null);
          setVariant(null);
        }}
        inputValue={term}
        onInputChange={(_event, value, reason) => {
          if (reason === 'input') setTerm(value);
        }}
        renderInput={(params) => <TextField {...params} required autoFocus label={t('mapping.product')} />}
      />
      {product !== null && variable && detail.data ? (
        <TextField
          select
          label={t('mapping.variant')}
          helperText={t('mapping.variantHint')}
          value={variantChoice}
          onChange={(e) => {
            setVariant(e.target.value);
            setBatch(null);
          }}
        >
          <MenuItem value="">{t('mapping.wholeVariable')}</MenuItem>
          {detail.data.variants.map((one) => (
            <MenuItem key={one.id} value={one.id}>
              {variantLabel(one, t)}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      {product !== null ? (
        <TextField
          select
          label={t('mapping.batch')}
          helperText={mapping.shopExpiryDate ? t('mapping.batchHintExpiry') : t('mapping.batchHint')}
          value={choice}
          onChange={(e) => setBatch(e.target.value)}
        >
          <MenuItem value="">{t('mapping.wholeProduct')}</MenuItem>
          {batches.map((one) => (
            <MenuItem key={one.id} value={one.id}>
              {batchLabel(one, t, language)}
            </MenuItem>
          ))}
          {mapping.shopExpiryDate !== null && sameExpiry === undefined ? (
            <MenuItem value="new">
              {t('mapping.newBatch', { date: formatDate(mapping.shopExpiryDate, language) })}
            </MenuItem>
          ) : null}
        </TextField>
      ) : null}
    </FormDialog>
  );
};
